import React from 'react';
import * as Sentry from '@sentry/react';

/**
 * Error boundary to catch rendering errors and prevent full app crash
 * Displays user-friendly error UI with debug info in development
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);
    Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    // `compact` mode renders an inline error inside the route area so that the
    // surrounding chrome (navbar, footer) stays usable. The full-screen mode
    // is the original app-shell fallback for top-level catastrophes.
    const compact = !!this.props.compact;
    const containerStyle = compact
      ? { minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }
      : { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0a0a0f', padding: 24 };

    return (
      <div style={containerStyle}>
        <div style={{ maxWidth: 500, textAlign: 'center' }}>
          <h1 style={{ color: '#E63946', fontSize: compact ? 22 : 28, fontWeight: 900, marginBottom: 12 }}>
            Something went wrong
          </h1>
          <p style={{ color: compact ? '#6B7280' : 'rgba(250,247,242,0.60)', fontSize: 15, marginBottom: 24, lineHeight: 1.6 }}>
            We hit an unexpected error loading this page. You can retry, head home, or use another section of the site.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '11px 24px',
                borderRadius: 8,
                background: '#E63946',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              Refresh Page
            </button>
            <a
              href="/"
              style={{
                padding: '11px 24px',
                borderRadius: 8,
                background: compact ? '#F3F4F6' : 'rgba(255,255,255,0.08)',
                color: compact ? '#111827' : 'rgba(250,247,242,0.80)',
                border: compact ? '1px solid #E5E7EB' : '1px solid rgba(255,255,255,0.12)',
                textDecoration: 'none',
                fontWeight: 700,
                fontSize: 14,
                display: 'inline-block',
              }}
            >
              Go Home
            </a>
          </div>
          {process.env.NODE_ENV === 'development' && (
            <details style={{ marginTop: 32, textAlign: 'left' }}>
              <summary style={{ color: '#E63946', cursor: 'pointer', fontWeight: 700 }}>
                Error details (dev only)
              </summary>
              <pre
                style={{
                  background: 'rgba(0,0,0,0.3)',
                  padding: 12,
                  borderRadius: 8,
                  marginTop: 12,
                  overflow: 'auto',
                  color: compact ? '#374151' : 'rgba(250,247,242,0.60)',
                  fontSize: 12,
                }}
              >
                {this.state.error?.toString()}
                {this.state.error?.stack}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
